<script setup>
import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {ChevronRight, Folder, FolderPlus, Users, FileText, Pencil, Eye, Folders, Ellipsis, Trash2, Layers} from 'lucide-vue-next'
import {bbox} from '@turf/turf'

import {Button} from '@/components/ui/button'
import {DialogConfirm} from '@/components/dialogs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])

const router = useRouter()
const route = useRoute()
const emit = defineEmits(['select'])
const props = defineProps({
  project: Object,
  openedProjectIds: Object
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

const isSelected = computed(() => routeProjectId.value === props.project.project_id)
const isOpened = computed(() => !!props.openedProjectIds?.[props.project.project_id])
const isHovered = ref(false)
const hoveredFromMap = computed(() => store.getters['boundary/getHoveredBoundaryProjectId'] === props.project.project_id)

const select = (project, level = 0) => {
  emit('select', project, level)
}

const selectProject = () => {
  router.push({ name: 'ProjectHome', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

  const openProject = (project) => {
    router.push({ name: 'ProjectHome', params: { org_id: project.organization_id, project_id: project.project_id } })
  }

const createSubProject = () => {
  router.push({ name: 'ProjectCreate', params: { org_id: props.project.organization_id, project_id: props.project.project_id }})
}

const editProject = () => {
  router.push({ name: 'ProjectEdit', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

const placeDetails = computed(() => store.getters['boundary/getPlaceDetails'](props.project.project_id))

const hasGeometry = computed(() => {
  const boundaries = store.getters['boundary/getBoundaries']
  if (!boundaries?.features) return false
  const boundary = boundaries.features.find(
    f => f.properties.project_id === props.project.project_id
  )
  return !!boundary?.geometry
})

const zoomOnBoundary = async () => {
  try {
    const boundary = await store.dispatch('boundary/fetchProjectBoundary', props.project.project_id)
    if (!boundary || !boundary.geometry) return
    map.value.fitBounds(bbox(boundary))
  } catch (error) {
    console.error('Failed to fetch project boundary', error)
  }
}

const goToMembers = () => {
  router.push({ name: 'ProjectMembers', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

const goToTemplates = () => {
  router.push({ name: 'ProjectTemplates', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

const isoToEmoji = (isoCode) => {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

const createProject = () => {
  router.push({ name: 'ProjectCreate', params: { org_id: props.project.organization_id, project_id: props.project.project_id }})
}

const deleteProject = async () => {
  await store.dispatch('project/deleteProject', props.project)
  await store.dispatch('project/fetchProjects')
  if (isSelected.value) {
    router.push({ name: 'Dashboard' })
  }
}

const addToLayers = async () => {
  try {
    const layer = await store.dispatch('layers/createProjectDataLayer', {
      orgId: props.project.organization_id,
      projectId: props.project.project_id,
      projectName: props.project.project_name
    })
    if (layer) {
      router.push({ name: 'OrgLayers', params: { org_id: props.project.organization_id } })
    }
  } catch (error) {
    console.error('Failed to create project data layer:', error)
  }
}
</script>

<template>
  <div v-if="project?.project_id" class="flex items-center px-0.5">
    <div
      class="flex items-center flex-1 w-full rounded-lg p-0.5"
      :class="{'bg-secondary': (isHovered || hoveredFromMap) && !isSelected, 'bg-primary text-white': isSelected}"
      :value="project"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <Button
        variant="ghostDark"
        class="w-7 h-7 rounded-full p-0 text-muted-foreground hover:text-black project-expand-toggle"
        :class="{'opacity-0': !project.children?.length, 'rotate-90 text-black': isOpened, 'text-white': isSelected}"
        :id="`project-expand-${project.project_id}`"
        :aria-label="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        :aria-expanded="isOpened"
        :aria-controls="`project-children-${project.project_id}`"
        :title="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        type="button"
        :disabled="!project.children?.length"
        @click.stop
        @click="select(project)"
      >
        <ChevronRight class="w-7 h-5" :aria-hidden="true" />
      </Button>
      <div class="flex items-center justify-between w-full min-w-[200px]">
        <Button
          variant="ghost"
          class="flex items-center h-full p-0.5 cursor-pointer min-w-0 flex-1 justify-start hover:bg-transparent"
          :class="{'truncate': isHovered}"
          :id="`project-name-${project.project_id}`"
          :aria-label="`Open ${project.project_name} project`"
          :title="`Open ${project.project_name} project`"
          type="button"
          @click.stop="openProject(project)"
        >
          <div class="w-5 h-5 mr-2 flex items-center justify-center">
            <span v-if="project.emoji || placeDetails" class="text-lg leading-none">
              {{ project.emoji || (placeDetails ? isoToEmoji(placeDetails.country_code) : '') }}
            </span>
            <Folder v-if="!project.children?.length && !project.emoji" class="w-4.5 h-4.5 text-foreground" :class="{'text-white': isSelected}" />
            <Folders v-else-if="!placeDetails && !project.emoji" class="w-4.5 h-4.5 text-foreground" :class="{'text-white': isSelected}" />
          </div>
          <span class="truncate" :title="project.project_name">
            {{ project.project_name }}
          </span>
        </Button>

        <div class="flex items-center justify-end project-actions" :class="{'opacity-0': !isHovered && !isSelected}" @click.stop>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghostDark"
                class="w-7 h-7 rounded-full p-0 text-muted-foreground hover:text-black"
                :class="{'text-white': isSelected}"
                type="button"
              >
                <Ellipsis class="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-48">
              <DropdownMenuGroup>
                <div :title="!hasGeometry ? 'Add a location to zoom to this project' : undefined">
                  <DropdownMenuItem @click="zoomOnBoundary" class="cursor-pointer" :disabled="!hasGeometry">
                    <Eye class="w-4 h-4 mr-2" />
                    <span>View on Map</span>
                  </DropdownMenuItem>
                </div>
                <DropdownMenuItem @click="addToLayers" class="cursor-pointer">
                  <Layers class="w-4 h-4 mr-2" />
                  <span>View Data</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="createProject" class="cursor-pointer">
                  <FolderPlus class="w-4 h-4 mr-2" />
                  <span>New Sub-project</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="editProject" class="cursor-pointer">
                  <Pencil class="w-4 h-4 mr-2" />
                  <span>Edit</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem @click="goToMembers" class="cursor-pointer">
                  <Users class="w-4 h-4 mr-2" />
                  <span>Members</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="goToTemplates" class="cursor-pointer">
                  <FileText class="w-4 h-4 mr-2" />
                  <span>Templates</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DialogConfirm
                  @confirm="deleteProject"
                  :title="'Delete ' + project.project_name"
                  description="Are you sure you want to delete this project? This action cannot be undone."
                >
                  <DropdownMenuItem class="cursor-pointer text-red-600 focus:text-red-600" @select.prevent>
                    <Trash2 class="w-4 h-4 mr-2" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DialogConfirm>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  </div>

  <div
    class="pl-4 pr-1"
    v-if="isOpened"
    :id="`project-children-${project.project_id}`"
    role="group"
    :aria-label="`Sub-projects of ${project.project_name}`"
  >
    <ProjectListItem
      v-for="child in project.children"
      :key="child.project_id"
      :project="child"
      :opened-project-ids="openedProjectIds"
      @select="(project, level) => select(project, level + 1)"
    />
  </div>
</template>
